import BrowserRouter from '@fuse/core/BrowserRouter';
import FuseLayout from '@fuse/core/FuseLayout';
import FuseTheme from '@fuse/core/FuseTheme';
import { SnackbarProvider } from 'notistack';
import { useSelector } from 'react-redux';
import rtlPlugin from 'stylis-plugin-rtl';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { selectCurrentLanguageDirection } from 'app/store/i18nSlice';
import { selectUser } from 'app/store/userSlice';
import themeLayouts from 'app/theme-layouts/themeLayouts';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import FuseAuthorization from '@fuse/core/FuseAuthorization';
import settingsConfig from 'app/configs/settingsConfig';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import withAppProviders from './withAppProviders';
import { AuthProvider } from './auth/AuthContext';
import GlobalAlertDialog from './shared-components/alertDialog/GlobalAlertDialog';
import GlobalModal from './shared-components/modal/GlobalModal';
import 'react-photo-view/dist/react-photo-view.css';
import BackHandler from './shared-components/backHandler/BackHandler';
import { ApolloProvider } from './apolloClient';

// import axios from 'axios';
/**
 * Axios HTTP Request defaults
 */
// axios.defaults.baseURL = "";
// axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
// axios.defaults.headers.common['Content-Type'] = 'application/x-www-form-urlencoded';

const emotionCacheOptions = {
  rtl: {
    key: 'muirtl',
    stylisPlugins: [rtlPlugin],
    insertionPoint: document.getElementById('emotion-insertion-point'),
  },
  ltr: {
    key: 'muiltr',
    stylisPlugins: [],
    insertionPoint: document.getElementById('emotion-insertion-point'),
  },
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      staleTime: 1000,
    },
    mutations: {
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  const user = useSelector(selectUser);
  const langDirection = useSelector(selectCurrentLanguageDirection);
  const mainTheme = useSelector(selectMainTheme);

  return (
    <ApolloProvider>
      <CacheProvider value={createCache(emotionCacheOptions[langDirection])}>
        <FuseTheme theme={mainTheme} direction={langDirection}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <BrowserRouter>
                <BackHandler />
                <GlobalAlertDialog />
                <GlobalModal queryClient={queryClient} />
                <FuseAuthorization
                  userRole={user.role}
                  loginRedirectUrl={settingsConfig.loginRedirectUrl}
                >
                  <SnackbarProvider
                    maxSnack={5}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    classes={{
                      containerRoot: 'bottom-0 right-0 mb-52 md:mb-68 mr-8 lg:mr-80 z-99',
                    }}
                  >
                    <FuseLayout layouts={themeLayouts} />
                  </SnackbarProvider>
                  <ReactQueryDevtools initialIsOpen={false} />
                </FuseAuthorization>
              </BrowserRouter>
            </AuthProvider>
          </QueryClientProvider>
        </FuseTheme>
      </CacheProvider>
    </ApolloProvider>
  );
};

export default withAppProviders(App)();
